export default class CustomUploadAdapter {
  private loader: any;
  private static readonly MAX_IMAGES = 20;
  private static readonly MAX_TOTAL_SIZE_MB = 50;
  private static readonly MAX_TOTAL_SIZE_BYTES = CustomUploadAdapter.MAX_TOTAL_SIZE_MB * 1024 * 1024;

  constructor(loader: any) {
    this.loader = loader;
  }

  private getCurrentImageCount(): number {
    // Count actual images in the editor content
    const editorContent = (window as any).currentEditorContent || '';
    const imgRegex = /<img[^>]+src=['"]([^'">]+)['"]/g;
    let match;
    let count = 0;
    
    while ((match = imgRegex.exec(editorContent))) {
      count++;
    }
    
    return count;
  }

  private getCurrentTotalSize(): number {
    // Calculate size based on images currently in editor
    const pendingImages = (window as any).pendingContestImages || [];
    const serverImages = (window as any).serverImages || [];
    const editorContent = (window as any).currentEditorContent || '';
    
    // Extract image sources from editor content
    const imgRegex = /<img[^>]+src=['"]([^'">]+)['"]/g;
    const editorImageSrcs: string[] = [];
    let match;
    
    while ((match = imgRegex.exec(editorContent))) {
      editorImageSrcs.push(match[1]);
    }
    
    // Filter pending images to only include those still in the editor
    const activePendingImages = pendingImages.filter((file: File) => {
      const blobUrl = URL.createObjectURL(file);
      const blobId = blobUrl.split('/').pop();
      return editorImageSrcs.some(src => src.includes(blobId || ''));
    });
    
    // Filter server images to only include those still in the editor
    const activeServerImages = serverImages.filter((image: any) => {
      return editorImageSrcs.some(src => src.includes(image.fileUrl || ''));
    });
    
    // Calculate size from both pending and server images
    const pendingSize = activePendingImages.reduce((total: number, file: File) => total + file.size, 0);
    const serverSize = activeServerImages.reduce((total: number, image: any) => total + (image.fileSize || 0), 0);
    
    return pendingSize + serverSize;
  }

  private validateImageUpload(file: File): { isValid: boolean; error?: string } {
    // Check image count limit
    const currentCount = this.getCurrentImageCount();
    if (currentCount >= CustomUploadAdapter.MAX_IMAGES) {
      return {
        isValid: false,
        error: `Maximum ${CustomUploadAdapter.MAX_IMAGES} images allowed. You have already uploaded ${currentCount} images.`
      };
    }

    // Check total size limit
    const currentSize = this.getCurrentTotalSize();
    const newTotalSize = currentSize + file.size;
    
    if (newTotalSize > CustomUploadAdapter.MAX_TOTAL_SIZE_BYTES) {
      const currentSizeMB = (currentSize / (1024 * 1024)).toFixed(2);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return {
        isValid: false,
        error: `Total image size cannot exceed ${CustomUploadAdapter.MAX_TOTAL_SIZE_MB}MB. Current size: ${currentSizeMB}MB, this file: ${fileSizeMB}MB.`
      };
    }

    // Check individual file size (optional - you can add this if needed)
    // const maxFileSizeMB = 10; // 10MB per file
    // const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
    // if (file.size > maxFileSizeBytes) {
    //   return {
    //     isValid: false,
    //     error: `Individual file size cannot exceed ${maxFileSizeMB}MB.`
    //   };
    // }

    return { isValid: true };
  }

  async upload() {
    const file = await this.loader.file;

    // Validate upload restrictions
    const validation = this.validateImageUpload(file);
    if (!validation.isValid) {
      // Reject the upload and show error
      throw new Error(validation.error);
    }

    // Instant preview inside editor
    const objectUrl = URL.createObjectURL(file);

    // Store temporarily (for later upload after contest save)
    (window as any).pendingContestImages = (window as any).pendingContestImages || [];
    (window as any).pendingContestImages.push(file);

    return {
      default: objectUrl, // 👈 This ensures CKEditor can show the image immediately
    };
  }

  abort() {
    // If upload is canceled, you can clean up
  }
}

